import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  SectionCard,
  Input,
  Typography,
  SlimButton,
  Table,
  Tooltip,
  HeadItemType,
  Icon
} from 'components'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import { useDispatch, useSelector } from 'store/store'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import {
  checkOVBindStatus,
  getOvBinds,
  ovBindOperation,
  resetBindValidationStatus
} from 'store/OVContext/ovSlice'

import * as S from './styles'
import { formatDate } from 'commons/utils/date'
import { theme } from 'styles/theme/hav-theme'

export const OVBindSection = () => {
  const dispatch = useDispatch()
  const { ovNumber } = useParams()
  const [ovNumberInput, setOvNumberInput] = useState('')
  const [unbindModal, setUnbindModal] = useState(false)
  const [ovToUnbind, setOvToUnbind] = useState('')
  const {
    loadingBind,
    validationSuccess,
    validationError,
    bindOvsList,
    bindTotalizers,
    ovSelected: {
      Cabecalho: { CodigoStatus }
    }
  } = useSelector((state) => state.ov)

  const closeUnbindModal = () => setUnbindModal(false)

  const bindInputOV = () => {
    dispatch(
      ovBindOperation({
        tipoOperacao: 'I',
        numeroordemvenda: ovNumber ?? '',
        numeroordemvendarefe: ovNumberInput
      })
    )
    setOvNumberInput('')
  }

  const unbindOV = (selectedOV: string) => {
    dispatch(
      ovBindOperation({
        tipoOperacao: 'E',
        numeroordemvenda: ovNumber ?? '',
        numeroordemvendarefe: selectedOV
      })
    )
  }

  const tableHead: HeadItemType[] = [
    { label: 'Data da OV', columnName: 'DataOV' },
    { label: 'Mês cota', columnName: 'MesCota' },
    { label: 'OV Portal', columnName: 'OVPortal' },
    { label: 'OV SAP', columnName: 'OVSAP' },
    { label: 'Fábrica', columnName: 'Fabrica' },
    { label: 'Qtd.', columnName: 'Qtd' },
    { label: 'Valor (R$)', columnName: 'Valor' },
    { label: 'Prev. Faturamento', columnName: 'PrevFaturamento' },
    { label: 'Prev. Entrega', columnName: 'PrevEntrega' },
    { label: 'Ações', columnName: 'Acoes' }
  ]

  const tableData = () => {
    return bindOvsList.map((ov) => {
      return {
        id: ov.NumeroOrdemVenda,
        content: [
          ov.DataDigitacao,
          formatDate(ov.MesCota, 'MM/YYYY', 'YYYYMM'),
          ov.NumeroOrdemVenda,
          ov.NumeroOrdemVendaSap,
          ov.CodigoCentroMaterial,
          numberToDecimalString(ov.QuantidadeTotalItem),
          formatToPTBRNumber(ov.ValorTotalItem),
          ov.DataPrevistaFaturamento,
          ov.DataPrevistaEntrega,
          <S.ActionButtonWrapper key={`unbind-${ov.NumeroOrdemVenda}`}>
            <Tooltip title={'Desvincular OV'} placement="top" arrow>
              <S.ActionButton
                id={`unbind-${ov.NumeroOrdemVenda}-button`}
                onClick={() => {
                  setOvToUnbind(ov.NumeroOrdemVenda)
                  setUnbindModal(true)
                }}
                disabled={loadingBind || +CodigoStatus !== 0}
              >
                <Icon icon="cancel" color={theme.colors.neutral.white} size="15" />
              </S.ActionButton>
            </Tooltip>
          </S.ActionButtonWrapper>
        ]
      }
    })
  }

  useEffect(() => {
    dispatch(getOvBinds(ovNumber ?? ''))
  }, [])

  useEffect(() => {
    if (ovNumberInput.length === 15) {
      dispatch(
        checkOVBindStatus({
          numeroOrdemVenda: ovNumber ?? '',
          numeroOrdemVendaRefe: ovNumberInput
        })
      )
    } else {
      dispatch(resetBindValidationStatus())
    }
  }, [ovNumberInput])

  return (
    <S.CardWrapper>
      <SectionCard
        title="Vincular OVs YPEM"
        tooltip
        tooltipText="Esta é uma ação necessária e obrigatória para o registro da OV, pois não é possível enviar uma OV YRBA sem vinculá-la a nenhuma OV YPEM."
      >
        <S.ContentWrapper>
          <Typography design="hav-default-03-text">
            Adicione as OVs necessárias para gerar a YRBA:
          </Typography>
          <S.InsertRow>
            <S.InputWrapper>
              <Input
                id="ov-bind-input"
                label="Nº da OV Portal"
                placeholder="Digite aqui"
                value={ovNumberInput}
                width={180}
                required
                successOutline={!!validationSuccess}
                helperText={validationSuccess}
                error={validationError}
                maxLength={15}
                disabled={loadingBind || +CodigoStatus !== 0}
                onChange={(e) => setOvNumberInput(e.target.value)}
              />
            </S.InputWrapper>
            <S.ButtonWrapper>
              <SlimButton
                id="bind-ov-button"
                variant="primary"
                height={30}
                content="Adicionar"
                onClick={bindInputOV}
                loading={loadingBind}
                disabled={!validationSuccess || +CodigoStatus !== 0}
              />
            </S.ButtonWrapper>
          </S.InsertRow>
          {!!bindOvsList.length && (
            <>
              <S.TableWrapper>
                <Table
                  headData={tableHead}
                  rowHeight={40}
                  bodyData={tableData()}
                  customAlign={[{ colIndex: 7, textAlign: 'center' }]}
                />
              </S.TableWrapper>
              <S.TotalizerWrapper>
                <S.TotalizerContainer>
                  <S.TotalizerItem>
                    <Typography design="hav-h7-text">Qtd. total</Typography>
                    <span>{numberToDecimalString(bindTotalizers.QuantidadeTotal)}</span>
                  </S.TotalizerItem>
                  <S.TotalizerItem>
                    <Typography design="hav-h7-text">Valor total</Typography>
                    <span>{formatToPTBRNumber(bindTotalizers.ValorTotal)}</span>
                  </S.TotalizerItem>
                </S.TotalizerContainer>
              </S.TotalizerWrapper>
            </>
          )}
        </S.ContentWrapper>
      </SectionCard>
      {unbindModal && (
        <ModalContentLayout
          title="Desvincular OV"
          titleSize={22}
          width={480}
          description="Essa ação irá desvincular essa OV YPEM do seu pedido."
          closeButton
          closeAction={closeUnbindModal}
        >
          <S.ModalActions>
            <SlimButton variant="secondary" content="Voltar" onClick={closeUnbindModal} />
            <SlimButton
              variant="danger"
              content="Desvincular"
              onClick={() => {
                unbindOV(ovToUnbind)
                closeUnbindModal()
                setOvToUnbind('')
              }}
            />
          </S.ModalActions>
        </ModalContentLayout>
      )}
    </S.CardWrapper>
  )
}
